"use client";

import React, { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { HelpCircle } from "lucide-react";
import { ButtonGhost, ButtonPrimary } from "@/components/common/StandardButton";

interface OTPVerificationProps {
  phoneNumber: string;
  onVerify: (otp: string) => void;
  onResend: () => void;
  isLoading?: boolean;
  error?: string;
  className?: string;
}

export function OTPVerification({
  phoneNumber,
  onVerify,
  onResend,
  isLoading = false,
  error,
  className,
}: OTPVerificationProps) {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Timer countdown
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const handleInputChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Only take the last character
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
      setFocusedIndex(index + 1);
    }

    // Auto-submit when all fields are filled
    if (newOtp.every((digit) => digit !== "") && newOtp.join("").length === 6) {
      onVerify(newOtp.join(""));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Handle backspace
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setFocusedIndex(index - 1);
    }
    // Handle arrow keys for navigation
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setFocusedIndex(index - 1);
    }
    if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
      setFocusedIndex(index + 1);
    }
  };

  const handleFocus = (index: number) => {
    setFocusedIndex(index);
  };

  const handleBlur = () => {
    setFocusedIndex(null);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");

    if (pastedData.length === 6) {
      const newOtp = pastedData.split("");
      setOtp(newOtp);
      onVerify(pastedData);
    }
  };

  const handleResend = () => {
    if (canResend) {
      setTimer(30);
      setCanResend(false);
      setOtp(["", "", "", "", "", ""]);
      setFocusedIndex(0);
      inputRefs.current[0]?.focus();
      onResend();
    }
  };

  const handleManualVerify = () => {
    const otpString = otp.join("");
    if (otpString.length === 6) {
      onVerify(otpString);
    }
  };

  return (
    <div className={cn("flex flex-col gap-6 lg:gap-7 w-full", className)}>
      {/* Header Text */}
      <div className="flex flex-col gap-2 lg:gap-3 w-full text-left">
        <h1 className="text-brand text-[26px] lg:text-[30px] xl:text-[32px] font-semibold leading-[1.2] font-['Public_Sans']">
          Enter OTP
        </h1>
        <p className="text-auth-text text-[15px] lg:text-[16px] leading-[1.4] font-normal font-['Public_Sans']">
          We&apos;ve sent an activation code to your phone number{" "}
          <span className="text-brand font-medium">{phoneNumber}</span>
        </p>
      </div>

      {/* OTP Input Fields */}
      <div className="flex flex-col gap-4 lg:gap-5 w-full">
        <label className="text-auth-text text-[14px] font-medium font-['Public_Sans'] sr-only">
          Enter 6-digit verification code
        </label>
        <div
          className="flex flex-row gap-3 lg:gap-4 justify-center"
          role="group"
          aria-labelledby="otp-label"
          aria-describedby="otp-description"
        >
          {otp.map((digit, index) => (
            <div
              key={index}
              className={cn(
                "w-[50px] h-[50px] lg:w-[58px] lg:h-[58px] xl:w-[62px] xl:h-[62px] border-2 rounded-xl lg:rounded-2xl relative flex items-center justify-center bg-auth-surface transition-all duration-200",
                focusedIndex === index
                  ? "border-brand shadow-theme-brand"
                  : digit
                    ? "border-brand bg-brand/5"
                    : "border-auth-border hover:border-brand/50",
              )}
            >
              <input
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onFocus={() => handleFocus(index)}
                onBlur={handleBlur}
                onPaste={handlePaste}
                className="w-full h-full bg-transparent text-center text-[18px] lg:text-[20px] xl:text-[22px] font-semibold text-foreground font-['Public_Sans'] border-none outline-none"
                disabled={isLoading}
                aria-label={`Digit ${index + 1} of 6`}
                aria-describedby={index === 0 ? "otp-description" : undefined}
              />
            </div>
          ))}
        </div>
        <p
          id="otp-description"
          className="text-auth-text-muted text-[13px] lg:text-[14px] text-center font-['Public_Sans']"
        >
          Enter the 6-digit code sent to your phone
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2.5">
          <p className="text-red-400 text-sm text-center font-['Public_Sans']">
            {error}
          </p>
        </div>
      )}

      {/* Verify Button */}
      <ButtonPrimary
        onClick={handleManualVerify}
        disabled={isLoading || otp.join("").length !== 6}
        loading={isLoading}
        size="lg"
        shape="pill"
        fullWidth
      >
        {isLoading ? "Verifying..." : "Verify Code"}
      </ButtonPrimary>

      {/* Resend Section */}
      <div className="flex flex-col gap-3 lg:gap-4 items-center">
        <div className="flex flex-row items-center gap-2">
          <div className="flex h-[20px] w-[20px] lg:h-[22px] lg:w-[22px] items-center justify-center rounded-full bg-brand/20">
            <HelpCircle className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-brand" />
          </div>
          <span className="font-['Public_Sans'] text-[14px] lg:text-[15px] font-normal text-auth-text">
            Didn&apos;t receive the code?
          </span>
        </div>

        {canResend ? (
          <ButtonGhost
            onClick={handleResend}
            disabled={isLoading}
            className="text-brand text-[14px] lg:text-[15px] font-semibold font-['Public_Sans'] hover:text-brand/90 transition-colors underline h-auto"
          >
            Resend Code
          </ButtonGhost>
        ) : (
          <span className="text-auth-text-muted text-[14px] lg:text-[15px] font-medium font-['Public_Sans']">
            Resend in {timer}s
          </span>
        )}
      </div>
    </div>
  );
}
